import { NGXLogger as LoggerService } from "ngx-logger";

import { CONNECTION_STALL_MS, CONNECTION_VERY_LONG_STALL_MS } from "./connection-status.service";
import type { ConnectionStatusService, ConnectionWindowReason } from "./connection-status.service";
import type { RouteContextService } from "./route-context.service";
import type { AppCheckState, AppCheckTelemetryService } from "../../whoo/core/app-check-telemetry.service";
import { warnToServer } from "./server-warn";
import { AUTH_STATE_KEY } from "./auth-state-storage";
import { LANG_PREF_KEY } from "./lang-pref";

/** Marker-Strings fuer die Error-Triage (known-errors.yaml) - exakt so, nicht umformulieren. */
export const CONNECTION_STALLED_MESSAGE = 'ConnectionStalled: no db connection after 10s';
export const CONNECTION_CONNECTED_AFTER_STALL_MESSAGE = 'ConnectionStalled: connected after stall';
export const CONNECTION_STILL_STALLED_MESSAGE = 'ConnectionStalled: still no db connection after 60s';

/**
 * Routes that need the database: report and banner only make sense where the user waits for data. Static pages
 * (feedback, imprint / privacy, 404, login) stay quiet (workorder-app-check-connection-stalled-hint-2026-10-26, decision 6).
 */
export const DB_ROUTE_IDS: ReadonlySet<string> = new Set([
    'vote', 'votePreview', 'voteResponse', 'votePreviewResponse',
    'pollResponses', 'pollMatrix', 'editPoll', 'createPoll', 'myPolls',
    'samplePoll', 'samplePollMatrix', 'samplePollsList',
]);

/**
 * localStorage keys only earlier visits leave behind: the GA consent answer, the auth flag of a signed-in (also
 * anonymous) session, an explicit language choice.
 */
const VISITED_BEFORE_KEYS = ['whoo:ga:consent', AUTH_STATE_KEY, LANG_PREF_KEY];
/**
 * where @firebase/database remembers a failed WebSocket: set to 'true' on EVERY socket open ("assume failure until
 * proven otherwise"), removed once the connection is healthy. Read at a stall it would almost always be 'true' - only
 * the value at page load says something: 'true' = the last attempt in this browser never got healthy, the SDK starts
 * this session with long polling (TransportManager.initTransports_).
 */
const PREVIOUS_WEBSOCKET_FAILURE_KEY = 'firebase:previous_websocket_failure';

/** the storage values of interest as they were at page load, before this session's SDK or app wrote anything */
export interface StartupStorageSnapshot {
    /** null without localStorage (Safari private mode, blocked storage) */
    prevWsFailure: boolean | null;
    visitedBefore: boolean | null;
}

export function readStartupStorage(): StartupStorageSnapshot {
    try {
        const storage = window.localStorage;
        return {
            prevWsFailure: storage.getItem(PREVIOUS_WEBSOCKET_FAILURE_KEY) === 'true',
            visitedBefore: VISITED_BEFORE_KEYS.some(key => storage.getItem(key) !== null),
        };
    } catch {
        return { prevWsFailure: null, visitedBefore: null };
    }
}

/** taken when the bundle is evaluated - before the bootstrap, the first database socket and the first auth emission */
const STARTUP_STORAGE: StartupStorageSnapshot = readStartupStorage();

/** the reCAPTCHA Enterprise loader @firebase/app-check inserts (`RECAPTCHA_ENTERPRISE_URL`) and index.html preloads */
const RECAPTCHA_SCRIPT_MARK = 'recaptcha/enterprise.js';
/** the large library the loader pulls from www.gstatic.com (`recaptcha__<lang>.js`) */
const RECAPTCHA_LIB_MARK = 'recaptcha__';

/** the reCAPTCHA load times of the ConnectionStalled reports (see ConnectionStalledFields) */
export interface RecaptchaResourceFields {
    recaptchaScriptAfterMs: number | null;
    recaptchaLibAfterMs: number | null;
    recaptchaScriptFromCache: boolean | null;
}

/**
 * Reads the reCAPTCHA load times from Resource Timing (workorder-app-check-recaptcha-preload-telemetry-2026-10-29,
 * decision 4): `responseEnd` of the first `enterprise.js` and the first `recaptcha__` entry relative to `startAtMs`
 * (negative = loaded before the window started), null without an entry. The cache flag needs the sizes, which a
 * cross-origin entry only carries with `Timing-Allow-Origin` - without them (both sizes 0) it is null.
 */
export function readRecaptchaResourceFields(entries: ReadonlyArray<PerformanceEntry>, startAtMs: number): RecaptchaResourceFields {
    const script = entries.find(entry => entry.name.includes(RECAPTCHA_SCRIPT_MARK)) as PerformanceResourceTiming | undefined;
    const lib = entries.find(entry => entry.name.includes(RECAPTCHA_LIB_MARK)) as PerformanceResourceTiming | undefined;
    const after = (entry: PerformanceResourceTiming | undefined) => entry ? Math.round(entry.responseEnd - startAtMs) : null;
    return {
        recaptchaScriptAfterMs: after(script),
        recaptchaLibAfterMs: after(lib),
        recaptchaScriptFromCache: !script || !(script.decodedBodySize > 0) ? null : script.transferSize === 0,
    };
}

/** the resource entries of this page; empty where Resource Timing is missing */
export function readResourceEntries(): ReadonlyArray<PerformanceEntry> {
    try {
        return typeof performance?.getEntriesByType === 'function' ? performance.getEntriesByType('resource') : [];
    } catch {
        return [];
    }
}

/**
 * A report timer that fires more than a second before its threshold (measured by performance.now()) runs in a headless
 * renderer with virtual time (crawler): both reports then arrive within milliseconds at `sinceStartMs` of about 2 s.
 */
export function isVirtualTimeSuspect(sinceStartMs: number, thresholdMs: number): boolean {
    return sinceStartMs < thresholdMs - 1000;
}

/** the fields of every ConnectionStalled report; times are integer milliseconds, null when unknown */
export interface ConnectionStalledFields {
    online: boolean;
    appCheck: AppCheckState;
    /** performance.now() at the start of the current stall window (see ConnectionStatusService class doc) */
    startAtMs: number;
    /** which window the report measures: `start` is the start metric, `disconnect` / `visible` / `online` a window after it */
    windowReason: ConnectionWindowReason;
    sinceStartMs: number;
    /** App Check token available, relative to startAtMs (negative = before the window started) */
    tokenAfterMs: number | null;
    /** Firebase Auth initialized, relative to startAtMs (negative = before the window started) */
    authAfterMs: number | null;
    /** only in the `connected after stall` report */
    connectedAfterMs?: number | null;
    hiddenAny: boolean;
    hiddenNow: boolean;
    prevWsFailure: boolean | null;
    visitedBefore: boolean | null;
    /** reCAPTCHA loader `enterprise.js` loaded (`responseEnd`), relative to startAtMs; null = not (yet) loaded */
    recaptchaScriptAfterMs: number | null;
    /** reCAPTCHA library `recaptcha__<lang>.js` loaded (`responseEnd`), relative to startAtMs; null = not (yet) loaded */
    recaptchaLibAfterMs: number | null;
    /** `enterprise.js` came without transfer (HTTP cache); null = no entry or no sizes (cross-origin without Timing-Allow-Origin) */
    recaptchaScriptFromCache: boolean | null;
    /** last App Check token error of the session, `<code>` or `<code> <httpStatus>`; null = none */
    appCheckError: string | null;
    /** the report timer fired more than 1 s before its threshold (virtual time, crawler) - the report is sent anyway */
    virtualTimeSuspect: boolean;
}

/**
 * Sends the ConnectionStalled telemetry (workorder-app-check-connection-stalled-telemetry-2026-10-27, decisions 2-4),
 * independent of the banner and of a rendered banner slot:
 *
 * - `ConnectionStalled: no db connection after 10s` when `stalled$` turns true while the route has database context
 *   (`DB_ROUTE_IDS`, route id at the moment of the stall);
 * - `ConnectionStalled: connected after stall` with `connectedAfterMs` when `.info/connected` turns true after that report,
 *   in the same window (workorder-app-check-connection-stalled-window-reset-2026-10-27, decision 6 - a connection in a
 *   later window would make `connectedAfterMs` relative to another start);
 * - `ConnectionStalled: still no db connection after 60s` when `stalledVeryLong$` turns true after that report.
 *
 * Each marker at most once per session, the two follow-ups only in sessions that sent the ten-second report (same
 * denominator, no route check of their own). All three go out via `warnToServer` with the same field object. No user
 * data, no URLs, no poll ids. Created once in the WhooAppComponent constructor (a single instance per page load).
 * `prevWsFailure` and `visitedBefore` are the localStorage values at page load (STARTUP_STORAGE, see the key docs).
 *
 * `exposeDebug` (dev builds only) puts `window.__whooConnectionDiag()` up - it returns the current fields, for
 * `scripts/verify-connection-stalled-dev.mjs` check 1 (normal case: `tokenAfterMs` / `authAfterMs` set, no report).
 *
 * Where the start time goes (workorder-app-check-recaptcha-preload-telemetry-2026-10-29, decision 4): the reCAPTCHA load
 * times from Resource Timing (`readRecaptchaResourceFields`), the last token error with its HTTP status (`appCheckError`)
 * and `virtualTimeSuspect` (`isVirtualTimeSuspect` against the threshold of the report: 10 s, 60 s). The
 * `connected after stall` report has no timer of its own and repeats the value of the ten-second report, as does the
 * debug function after that report (false before).
 */
export class ConnectionStalledReporter {
    private reportedStall = false;
    private reportedConnected = false;
    private reportedStillStalled = false;
    /** the window of the ten-second report */
    private stallWindowId: number | null = null;
    /** `virtualTimeSuspect` of the ten-second report */
    private stallVirtualTimeSuspect = false;

    constructor(
        private logger: LoggerService,
        private connectionStatus: Pick<ConnectionStatusService, 'stalled$' | 'stalledVeryLong$' | 'connected$' | 'diagnostics'>,
        private routeContext: Pick<RouteContextService, 'id'>,
        private appCheckTelemetry: Pick<AppCheckTelemetryService, 'state' | 'tokenAtMs' | 'authAtMs' | 'lastError'>,
        exposeDebug = false,
        private startupStorage: StartupStorageSnapshot = STARTUP_STORAGE,
        private resourceEntries: () => ReadonlyArray<PerformanceEntry> = readResourceEntries,
    ) {
        this.connectionStatus.stalled$.subscribe(stalled => { if (stalled) this.onStalled(); });
        this.connectionStatus.connected$.subscribe(connected => { if (connected === true) this.onConnected(); });
        this.connectionStatus.stalledVeryLong$.subscribe(stalled => { if (stalled) this.onStillStalled(); });
        if (exposeDebug && typeof window !== 'undefined') {
            (window as unknown as { __whooConnectionDiag?: () => ConnectionStalledFields }).__whooConnectionDiag = () => this.fields();
        }
    }

    private onStalled(): void {
        if (this.reportedStall || !DB_ROUTE_IDS.has(this.routeContext.id)) return;
        this.reportedStall = true;
        this.stallWindowId = this.connectionStatus.diagnostics.windowId;
        const fields = this.fields({ thresholdMs: CONNECTION_STALL_MS });
        this.stallVirtualTimeSuspect = fields.virtualTimeSuspect;
        warnToServer(this.logger, CONNECTION_STALLED_MESSAGE, fields);
    }

    private onConnected(): void {
        if (!this.reportedStall || this.reportedConnected) return;
        if (this.connectionStatus.diagnostics.connectedWindowId !== this.stallWindowId) return;
        this.reportedConnected = true;
        warnToServer(this.logger, CONNECTION_CONNECTED_AFTER_STALL_MESSAGE, this.fields({ withConnected: true }));
    }

    private onStillStalled(): void {
        if (!this.reportedStall || this.reportedStillStalled) return;
        this.reportedStillStalled = true;
        warnToServer(this.logger, CONNECTION_STILL_STALLED_MESSAGE, this.fields({ thresholdMs: CONNECTION_VERY_LONG_STALL_MS }));
    }

    /**
     * the report fields of this moment (see ConnectionStalledFields); `thresholdMs` = threshold of the report timer for
     * `virtualTimeSuspect`, without it the value of the ten-second report
     */
    public fields(options: { withConnected?: boolean; thresholdMs?: number } = {}): ConnectionStalledFields {
        const diagnostics = this.connectionStatus.diagnostics;
        const start = diagnostics.startAtMs;
        const relative = (at: number | null) => at === null ? null : Math.round(at - start);
        const sinceStartMs = Math.round(performance.now() - start);
        const fields: ConnectionStalledFields = {
            online: typeof navigator === 'undefined' ? true : navigator.onLine,
            appCheck: this.appCheckTelemetry.state,
            startAtMs: Math.round(start),
            windowReason: diagnostics.windowReason,
            sinceStartMs,
            tokenAfterMs: relative(this.appCheckTelemetry.tokenAtMs),
            authAfterMs: relative(this.appCheckTelemetry.authAtMs),
            hiddenAny: diagnostics.hiddenAny,
            hiddenNow: diagnostics.hiddenNow,
            prevWsFailure: this.startupStorage.prevWsFailure,
            visitedBefore: this.startupStorage.visitedBefore,
            ...readRecaptchaResourceFields(this.resourceEntries(), start),
            appCheckError: this.appCheckTelemetry.lastError,
            virtualTimeSuspect: options.thresholdMs === undefined
                ? this.stallVirtualTimeSuspect
                : isVirtualTimeSuspect(sinceStartMs, options.thresholdMs),
        };
        if (options.withConnected) fields.connectedAfterMs = relative(diagnostics.connectedAtMs);
        return fields;
    }
}
